import { Inject, Injectable, OnDestroy } from "@angular/core";
import { newGuid } from "moo-shared";
import { BehaviorSubject, catchError, filter, map, merge, Observable, of, take, throwError, timeout, timer } from "rxjs";
import { IGooglePrincipal, WINDOW, Window } from "../../../shared/app.window";

export const googlePrincipalIdentityKeyType = "KEY_TYPE_PASSWORD_PLAIN";
export const googlePrincipalPasswordBytes = "Moo";
const googlePrincipalTokenPrefix = "chromeDeviceToken";

export enum RegistrationState {
  Undefined,
  Added,
  CompletePending,
  Completed
}

export const chromeHandoffTimeoutMs = 10000;
// The initialize reply is an in-window postMessage round-trip answered by the native ChromeOS
// sign-in host (sub-millisecond), never a network call. 1s is therefore a wide margin on a real
// sign-in webview; the grace exists only so an in-session ChromeOS tab - which never gets a reply
// - hands off after 1s instead of waiting the full chromeHandoffTimeoutMs.
export const chromeSignInReplyGraceMs = 1000;

/**
 * Bridges the ChromeOS "credentials passing" API (window.google.principal, loaded from
 * https://ssl.gstatic.com/accounts/chrome/users-1.0.js via Views/Home/Index.cshtml) for
 * picture/QR logins, which have no typeable password for ChromeOS to scrape.
 *
 * Contract (chromium.org "Advanced integration for SAML SSO on Chrome devices"):
 *  - initialize -> add -> complete; only proceed once the initialize callback fires.
 *  - complete() must be called only AFTER the user is authenticated; calling it (or
 *    navigating away) too early tears the sign-in webview down and the credential is lost.
 *  - a fresh, opaque token correlates add <-> complete and avoids cross-attempt confusion.
 *
 * users-1.0.js defines window.google.principal unconditionally, on every browser and OS, as a
 * parser-blocking script in the document head - so it is present before Angular boots (or,
 * when gstatic is unreachable, never). Its presence therefore carries no ChromeOS signal.
 * The only genuine sign-in-webview signal is the initialize() reply (a gaia_saml_api_reply
 * postMessage, answered exclusively by the ChromeOS sign-in screen); the add()/complete()
 * callbacks are local setTimeout echoes, not ChromeOS acknowledgements.
 *
 * So suppressGoogleLoginForm() only registers the credential (initialize + add); the caller
 * invokes completeRegistration() from the authentication-success path. Progress is exposed
 * via registrationStateSubject as a small state machine
 * (Undefined -> Added | CompletePending -> Completed). The per-attempt token doubles as the
 * attempt identity: callbacks that captured a superseded token are ignored when the account
 * is switched (A -> B) before the previous flow finished.
 */
@Injectable()
export class ChromeDeviceService implements OnDestroy {
  private token = "";
  // guards against a second complete() while the first ack is still outstanding (state stays
  // Added until the async complete callback flips it to Completed). Reset per attempt in
  // suppressGoogleLoginForm, and when registrationCompleted() gives up - otherwise a retry on the
  // same attempt (no fresh suppressGoogleLoginForm, e.g. re-picking pictures for the same account)
  // could never re-issue complete() after an ack that timed out.
  private completing = false;
  // true once the initialize reply arrived, which only the ChromeOS sign-in screen sends. Sticky
  // for the page's lifetime: it is an environment fact, not per-attempt state.
  isSignInWebview = false;
  registrationStateSubject = new BehaviorSubject<RegistrationState>(RegistrationState.Undefined);

  constructor(@Inject(WINDOW) private window: Window) { }

  ngOnDestroy(): void {
    this.registrationStateSubject.complete();
  }

  suppressGoogleLoginForm(loginName: string) {
    // isSignInWebview is intentionally not reset here - it is a sticky environment fact (see field).
    this.completing = false;
    this.token = `${googlePrincipalTokenPrefix}-${newGuid()}`;
    this.registrationStateSubject.next(RegistrationState.Undefined);

    const principal = this.window.google?.principal;
    if (principal) {
      this.beginRegistration(principal, loginName, this.token);
    }
  }

  completeRegistration() {
    const principal = this.window.google?.principal;
    if (principal && this.registrationStateSubject.value === RegistrationState.Added && !this.completing) {
      this.complete(principal, this.token);
      return;
    }
    // Record the intent while the add is still pending; only from Undefined, so a repeated
    // call cannot regress a Completed registration.
    if (this.isChromeOsUserAgent() && this.registrationStateSubject.value === RegistrationState.Undefined) {
      this.registrationStateSubject.next(RegistrationState.CompletePending);
    }
  }

  /**
   * Resolves when it is safe to hand off (emit the token / navigate). Off ChromeOS: at once.
   * On ChromeOS: after the deferred complete(), bounded - and when the initialize reply has
   * not arrived within the grace period this is not the sign-in webview (e.g. an in-session
   * tab) and we hand off right away.
   */
  waitForChromeHandoff(): Observable<void> {
    if (!this.isChromeOsUserAgent()) {
      return of(undefined);
    }
    const completed = this.registrationCompleted().pipe(
      catchError(() => of(undefined)));
    const noSignInWebview = timer(chromeSignInReplyGraceMs).pipe(
      filter(() => !this.isSignInWebview),
      map(() => undefined));
    return merge(completed, noSignInWebview).pipe(take(1));
  }

  /**
   * Emits once the deferred complete() ran; errors (rxjs TimeoutError) when that does not happen
   * within chromeHandoffTimeoutMs.
   */
  registrationCompleted(): Observable<void> {
    return this.registrationStateSubject.pipe(
      filter(state => state === RegistrationState.Completed),
      take(1),
      timeout(chromeHandoffTimeoutMs),
      map(() => undefined),
      catchError(error => {
        this.completing = false;
        return throwError(() => error);
      }));
  }

  // "CrOS" is frozen by the UA-reduction spec ("X11; CrOS x86_64 14541.0.0")
  private isChromeOsUserAgent(): boolean {
    return this.window.navigator.userAgent.includes("CrOS");
  }

  private beginRegistration(principal: IGooglePrincipal, loginName: string, token: string): void {
    principal.initialize(() => {
      // The reply proves this is the ChromeOS sign-in webview - an environment fact for the whole
      // page, so record it even for a superseded attempt. Only the add/complete below stay
      // token-guarded.
      this.isSignInWebview = true;
      if (token !== this.token) {
        return;
      }
      principal.add(
        {
          token,
          user: loginName,
          passwordBytes: googlePrincipalPasswordBytes,
          keyType: googlePrincipalIdentityKeyType
        },
        () => this.onCredentialAdded(principal, token));
    });
  }

  private onCredentialAdded(principal: IGooglePrincipal, token: string): void {
    if (token !== this.token) {
      return;
    }
    if (this.registrationStateSubject.value === RegistrationState.CompletePending) {
      this.complete(principal, token);
    } else {
      this.registrationStateSubject.next(RegistrationState.Added);
    }
  }

  private complete(principal: IGooglePrincipal, token: string): void {
    this.completing = true;
    principal.complete({ token }, () => {
      if (token !== this.token) {
        return;
      }
      this.registrationStateSubject.next(RegistrationState.Completed);
    });
  }
}
